'use client';

import React, { useState, useEffect } from 'react';
import { EmployerBrandingBanner } from '@components/templates/employer/offer/EmployerBrandingBanner';
import { Trans, useTranslation } from 'next-i18next';
import { Button, Input } from '@components/common/Forms';
import { CheckIcon } from '@heroicons/react/16/solid';
import { TailwindSpinner } from '@components/common';
import { showBuyNeuroModal } from '@components/features/modals/buy-neuro/BuyNeuroModal';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useSwrAccountDiscountData } from '@hooks/useSwrApi/endpoints/order/useSwrAccountDiscountData';
import { useSwrBestieScreeningActivate } from '@hooks/useSwrApi/endpoints/bestie-screening/useSwrBestieScreeningActivate';
import { useCreditsCalculator } from '@hooks/credits/useCreditsCalculator';
import { useStaticToasts } from '@hooks/useStaticToasts/useStaticToasts';

interface OfferPageServicesTableProps {
  isAuth: boolean;
  currentDiscountPercentage?: number;
  className?: string;
}

type PremiumTier = '2k' | '5k' | '10k';
type BestiePack = 'pack1' | 'pack2' | 'pack3';
type BestiePackType = 'BESTIE-SCREENING-START' | 'BESTIE-SCREENING-GROWTH' | 'BESTIE-SCREENING-SCALE';

// Bestie.jobs pack configuration with prices and screening credits
const bestiePackConfig: Record<BestiePack, { price: number; screeningCredits: number; packType: BestiePackType }> = {
  pack1: { price: 100, screeningCredits: 100, packType: 'BESTIE-SCREENING-START' },
  pack2: { price: 400, screeningCredits: 500, packType: 'BESTIE-SCREENING-GROWTH' },
  pack3: { price: 1000, screeningCredits: 1500, packType: 'BESTIE-SCREENING-SCALE' },
};

export const OfferPageServicesTable = (props: OfferPageServicesTableProps) => {
  const { isAuth, currentDiscountPercentage, className = '' } = props;
  const { t } = useTranslation('common');
  const { router, locale } = useExtendedRouter();
  const { status200Toast } = useStaticToasts();

  const [selectedTier, setSelectedTier] = useState<PremiumTier>('2k');
  const [selectedBestiePack, setSelectedBestiePack] = useState<BestiePack>('pack1');
  const [creditAmount, setCreditAmount] = useState<number>(1000);

  // Account discount data for topup box (fetch for all users to show 0 values)
  const { data: accountDiscountData } = useSwrAccountDiscountData({ locale, fetchCondition: true });

  // Bestie screening activation hook
  const { trigger: triggerBestieActivate, isMutating: isBestieActivating } = useSwrBestieScreeningActivate({
    onSuccess: () => {
      status200Toast(t('flash.message.deal.success'));
    },
  });

  // Select the next discount tier by default based on currentDiscountPercentage
  useEffect(() => {
    if (currentDiscountPercentage === undefined || currentDiscountPercentage < 15) {
      setSelectedTier('2k');
    } else if (currentDiscountPercentage < 20) {
      setSelectedTier('5k');
    } else if (currentDiscountPercentage < 25) {
      setSelectedTier('10k');
    } else {
      // Already at max tier, keep 10k selected
      setSelectedTier('10k');
    }
  }, [currentDiscountPercentage]);

  // Credits calculator for topup box
  const { priceData, priceIsValidating } = useCreditsCalculator(creditAmount);

  // Calculate values for topup info display
  const lastYearValue = Math.round(priceData?.lastYearOrderValueEur ?? 0);
  const totalConsumption = creditAmount + lastYearValue;

  // Calculate the next discount tier based on totalConsumption using discountLevels
  // This always shows the next tier regardless of current discount level
  const discountLevels = priceData?.discountLevels ?? accountDiscountData?.discountLevels ?? [];
  const nextTier = discountLevels
    .filter((level) => (level.minAmount ?? 0) > totalConsumption)
    .sort((a, b) => (a.minAmount ?? 0) - (b.minAmount ?? 0))[0];

  const differenceToNextTier = nextTier ? Math.round((nextTier.minAmount ?? 0) - totalConsumption) : 0;
  const nextTierPercentage = nextTier?.percentage;
  const showNextTierSuggestion = nextTier && differenceToNextTier > 0;

  // Premium tier configuration
  const premiumTiers: Record<PremiumTier, { percentage: number; titleKey: string; benefits: string[]; minAmount: number }> = {
    '2k': {
      percentage: 15,
      titleKey: 'consultant.info.box.1.title',
      benefits: [
        t('premium.15.benefit.5.name'),
        t('premium.15.benefit.4.name'),
        t('premium.15.benefit.1.name'),
        t('premium.15.benefit.2.name'),
        t('premium.15.benefit.3.name'),
        t('premium.15.benefit.6.name'),
        t('premium.15.benefit.7.name'),
      ],
      minAmount: 2000,
    },
    '5k': {
      percentage: 20,
      titleKey: 'consultant.info.box.2.title',
      benefits: [
        t('premium.20.benefit.5.name'),
        t('premium.20.benefit.4.name'),
        t('premium.20.benefit.1.name'),
        t('premium.20.benefit.2.name'),
        t('premium.20.benefit.3.name'),
        t('premium.20.benefit.6.name'),
        t('premium.20.benefit.7.name'),
      ],
      minAmount: 5000,
    },
    '10k': {
      percentage: 25,
      titleKey: 'consultant.info.box.3.title',
      benefits: [
        t('premium.25.benefit.5.name'),
        t('premium.25.benefit.4.name'),
        t('premium.25.benefit.1.name'),
        t('premium.25.benefit.2.name'),
        t('premium.25.benefit.3.name'),
        t('premium.25.benefit.6.name'),
        t('premium.25.benefit.7.name'),
      ],
      minAmount: 10000,
    },
  };

  const currentTierData = premiumTiers[selectedTier];
  const isCurrentTierActive = currentDiscountPercentage !== undefined
    && currentDiscountPercentage >= currentTierData.percentage;

  const handleActivatePremium = () => {
    if (!isAuth) {
      void router.push(`/login?targetPath=${encodeURIComponent('/company/offer?scrollTo=premium-section')}`, { withLocale: false });
      return;
    }
    showBuyNeuroModal(currentTierData.minAmount);
  };

  const handleTopUp = () => {
    if (!isAuth) {
      void router.push(`/login?targetPath=${encodeURIComponent('/company/offer?scrollTo=wallet-bar')}`, { withLocale: false });
      return;
    }
    showBuyNeuroModal(creditAmount);
  };

  const handleBuyBestie = () => {
    if (!isAuth) {
      void router.push(`/login?targetPath=${encodeURIComponent('/company/offer')}`, { withLocale: false });
      return;
    }
    void triggerBestieActivate({ packType: bestiePackConfig[selectedBestiePack].packType });
  };

  return (
    <div className={`overflow-hidden rounded-lg bg-surface shadow-lg ${className}`}>
      <div className="grid grid-cols-1 lg:grid-cols-4">
        {/* Bestie.jobs with pack selection */}
        <div className="border-b border-gray-200 p-5 lg:border-b-0 lg:border-r">
          <div className="flex h-full flex-col">
            {/* Title with pack selection on the right */}
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-left font-bold">
                Bestie.jobs
              </h3>

              {/* Pack selection buttons - positioned on the right of the title */}
              <div className="flex gap-1">
                {(['pack1', 'pack2', 'pack3'] as BestiePack[]).map((pack) => (
                  <button
                    key={pack}
                    type="button"
                    onClick={() => setSelectedBestiePack(pack)}
                    className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                      selectedBestiePack === pack
                        ? 'bg-secondary text-white'
                        : 'bg-surface-100 text-ink hover:bg-surface-200'
                    }`}
                  >
                    {t(`services.bestie.${pack}`)}
                  </button>
                ))}
              </div>
            </div>

            <p className="text-sm text-ink-medium">
              {t('services.bestie.subtitle')}
            </p>

            {/* Benefits list */}
            <div className="mt-3 flex-1">
              <div className="flex flex-col gap-2 py-1 text-left text-sm">
                <div className="flex items-start gap-2">
                  <CheckIcon className="size-5 min-w-5 text-secondary" />
                  <span>{t('services.bestie.jobs.line1')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckIcon className="size-5 min-w-5 text-secondary" />
                  <span>{t('services.bestie.jobs.line2')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckIcon className="size-5 min-w-5 text-secondary" />
                  <span>
                    <Trans
                      i18nKey="services.bestie.jobs.line3"
                      values={{ screeningCredits: bestiePackConfig[selectedBestiePack].screeningCredits }}
                    />
                  </span>
                </div>
              </div>
            </div>

            {/* Price display */}
            <div className="mt-5 flex items-baseline gap-2">
              <span className="text-3xl font-bold md:text-4xl">
                {bestiePackConfig[selectedBestiePack].price}
              </span>
              <span className="text-sm text-ink-medium">
                {t('employer.branding.price.desc')}
              </span>
            </div>

            {/* Buy button */}
            <Button
              styling="solid"
              color="secondary"
              rounding="full"
              size="sm"
              className="mt-3 w-fit"
              isLoading={isBestieActivating}
              onClick={handleBuyBestie}
            >
              {t('dashboard.label.buy_now')}
            </Button>
          </div>
        </div>

        {/* Employer Branding */}
        <div className="border-b border-gray-200 p-5 lg:border-b-0 lg:border-r">
          <EmployerBrandingBanner />
        </div>

        {/* Premium with tier selection */}
        <div className="flex flex-col border-b border-gray-200 p-5 lg:border-b-0 lg:border-r">
          {/* Title with tier selection on the right */}
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="text-left font-bold">
              {t(premiumTiers[selectedTier].titleKey)}
            </h3>

            {/* Tier selection buttons - positioned on the right of the title */}
            <div className="flex gap-1">
              {(['2k', '5k', '10k'] as PremiumTier[]).map((tier) => {
                const tierPercentage = premiumTiers[tier].percentage;
                const isTierActive = currentDiscountPercentage !== undefined
                  && currentDiscountPercentage >= tierPercentage;

                return (
                  <button
                    key={tier}
                    type="button"
                    onClick={() => setSelectedTier(tier)}
                    className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                      selectedTier === tier
                        ? 'bg-secondary text-white'
                        : 'bg-surface-100 text-ink hover:bg-surface-200'
                    }`}
                  >
                    {tier.toUpperCase()}
                    {isTierActive && ' ✓'}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Benefits list */}
          <div className="flex-1">
            <div className="flex flex-col gap-2 py-1 text-left text-sm">
              {currentTierData.benefits.map((benefit) => (
                <div key={benefit} className="flex items-start gap-2">
                  <CheckIcon className="size-5 min-w-5 text-secondary" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Activate button */}
          {!isCurrentTierActive && (
            <div>
              <Button
                styling="none"
                rounding="full"
                size="sm"
                className="block w-fit rounded-full bg-secondary px-6 py-3 text-sm font-medium text-surface"
                onClick={handleActivatePremium}
              >
                {t('subscription.package.activate')}
              </Button>
            </div>
          )}
        </div>

        {/* Wallet Top Up - Enhanced */}
        <div className="flex flex-col p-5">
          <h3 className="mb-3 text-left font-bold">
            {t('wallet.top-up.order')}
          </h3>

          {/* Order neuro input */}
          <div className="mb-4">
            <Input
              id="topUpAmountTable"
              type="number"
              value={creditAmount || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const val = e.target.value;
                if (val === '' || val === null) {
                  setCreditAmount(0);
                  return;
                }
                const numVal = parseInt(val, 10);
                if (!Number.isNaN(numVal)) {
                  setCreditAmount(numVal);
                }
              }}
              className="w-full"
            />
            <span className="mt-1 block text-xs text-ink-medium">
              {t('neuro.label.order-amount-no-discount')}
            </span>
            {showNextTierSuggestion && (
              <button
                type="button"
                onClick={() => setCreditAmount(creditAmount + differenceToNextTier)}
                className="mt-1 text-left text-xs text-secondary hover:underline"
              >
                <Trans
                  i18nKey="page.offer-configurator.supplement.desc"
                  values={{
                    nrOfCredits: differenceToNextTier,
                    nrPercentageDiscount: nextTierPercentage,
                  }}
                />
              </button>
            )}
          </div>

          <div className="mb-4 rounded-md bg-surface-100 p-2 text-sm">
            <div className="flex items-baseline justify-between gap-2 py-1 font-thin">
              <div>{t('price.calculator.euro.buy.history')}</div>
              <div>
                {priceIsValidating
                  ? <TailwindSpinner className="size-4 text-ink-medium" />
                  : lastYearValue}
              </div>
            </div>

            <div className="flex items-baseline justify-between gap-2 py-1 font-thin">
              <div>{t('price.calculator.euro.buy.total')}</div>
              <div>
                {priceIsValidating
                  ? <TailwindSpinner className="size-4 text-ink-medium" />
                  : totalConsumption}
              </div>
            </div>

            <div className="flex items-baseline justify-between gap-2 py-1 font-thin">
              <div>{t('price.calculator.euro.current.discount')}</div>
              <div>
                {priceIsValidating
                  ? <TailwindSpinner className="size-4 text-ink-medium" />
                  : (
                    <>
                      {priceData?.currentDiscountPercentage ?? 0}
                      %
                    </>
                  )}
              </div>
            </div>

            <div className="flex items-baseline justify-between gap-2 py-1 font-thin">
              <div>{t('price.calculator.euro.new.discount')}</div>
              <div>
                {priceIsValidating
                  ? <TailwindSpinner className="size-4 text-ink-medium" />
                  : (
                    <>
                      {priceData?.nextDiscountPercentage ?? 0}
                      %
                    </>
                  )}
              </div>
            </div>

            <div className="flex items-baseline justify-between gap-2 py-1 font-thin">
              <div>{t('price.calculator.new.discount.available')}</div>
              <div className="whitespace-nowrap text-right">
                {priceIsValidating
                  ? <TailwindSpinner className="size-4 text-ink-medium" />
                  : (priceData?.currentDiscountLevelExpirationDate ?? '-')}
              </div>
            </div>

          </div>

          <div className="mt-auto grid grid-cols-2 items-baseline gap-2 pt-5">
            <span className="text-sm text-ink-medium">{t('page.offer.calculator.total_net.euro')}</span>
            <div className="text-right text-2xl font-bold text-ink">
              {priceIsValidating
                ? <TailwindSpinner className="ml-auto size-6 text-ink-medium" />
                : (
                  <>
                    {priceData?.priceToPay ?? creditAmount}
                    {' '}
                    €
                  </>
                )}
            </div>
          </div>

          {/* Buy button */}
          <div className="mt-3">
            <Button
              onClick={handleTopUp}
              rounding="full"
              color="secondary"
              size="sm"
              className="w-fit"
            >
              {t('global.buy-neuro')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
